'use client'

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import Flag from 'react-flagkit'
import { BookOpenTextIcon, FileIcon, ImageIcon, LanguagesIcon, PlusCircleIcon, SaveIcon, XIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Editor } from '@/components/ui/editor'
import { Separator } from '@/components/ui/separator'
import { FileSelector } from '@/components/partials/files'
import { useMetaStore } from '@/store'
import {
  PageFileParams,
  PagePictureParams,
  PageTranslationParam,
  PageTranslationsParam,
  PageType,
  CreatePageParams
} from '@/types/page'
import { FileType } from '@/types/file'
import { LoadingIndicator, UnsavedChangesGuardDialog } from '@/components/partials'
import { FormModeEnum } from '@/enums'
import { setValidationErrors } from '@/lib/utils'
import { useScrollToError, useUnsavedChangesGuard } from '@/hooks'

type PropsType = {
  initialData?: PageType | null
  onSubmit: (data: CreatePageParams) => void
  onSubmitAndNew?: (data: CreatePageParams) => void
  onCancel: () => void
  isLoading?: boolean
  mode: FormModeEnum
}

export type PageFormRef = {
  handleValidationErrors: (error: any) => void
  reset: () => void
}

type FormData = {
  translations: PageTranslationsParam
}

const PageForm = forwardRef<PageFormRef, PropsType>(
  ({ initialData, onSubmit, onSubmitAndNew, onCancel, isLoading = false, mode }, ref) => {
    const t = useTranslations()
    const { scrollToFirstError } = useScrollToError()

    const { metaState } = useMetaStore()
    // Wrap the metaState.response?.locales in its own useMemo to prevent unnecessary re-renders
    const localesData = useMemo(() => metaState.response?.locales, [metaState.response])
    const locales = useMemo(() => localesData || [], [localesData])

    // State for pictures, files
    const [heroPicture, setHeroPicture] = useState<FileType | null>(initialData?.heroPicture || null)
    const [pictures, setPictures] = useState<FileType[]>(initialData?.pictures?.map(p => p.file) || [])
    const [files, setFiles] = useState<FileType[]>(initialData?.files?.map(f => f.file) || [])

    // Wrapper functions to handle type conversion for FileSelector onChange
    const handleHeroPictureChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setHeroPicture(null)
      } else if ('length' in files) {
        setHeroPicture(files[0])
      } else {
        setHeroPicture(files)
      }
    }

    const handlePicturesChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setPictures([])
      } else if ('length' in files) {
        setPictures(files)
      } else {
        setPictures([files])
      }
    }

    const handleFilesChange = (files: FileType | FileType[] | null) => {
      if (files === null) {
        setFiles([])
      } else if ('length' in files) {
        setFiles(files)
      } else {
        setFiles([files])
      }
    }

    const getCountryCodeFromLocale = (localeCode: string): string => {
      const parts = localeCode.split('-')
      return parts.length > 1 ? parts[1] : parts[0].toUpperCase()
    }

    const hasLocaleError = (localeCode: string, field: keyof PageTranslationParam): boolean => {
      return !!formErrors.translations?.[localeCode]?.[field]
    }

    const schema = useMemo(() => {
      const translationsSchema: Record<string, z.ZodObject<{ title: z.ZodString; content: z.ZodString }>> = {}

      locales.forEach(locale => {
        translationsSchema[locale.fullCode] = z.object({
          title: z.string().min(1, t('not_blank')),
          content: z.string().min(1, t('not_blank'))
        })
      })

      return z.object({
        translations: z.object(translationsSchema)
      })
    }, [locales, t])

    const getDefaultValues = useCallback((): FormData => {
      const defaultTranslations: PageTranslationsParam = {}

      locales.forEach(locale => {
        // Ensure values are always strings, never undefined
        const translation = initialData?.translations?.[locale.fullCode]
        defaultTranslations[locale.fullCode] = {
          title: translation?.title ?? '',
          content: translation?.content ?? ''
        }
      })

      return {
        translations: defaultTranslations
      }
    }, [locales, initialData])

    type SchemaType = z.infer<typeof schema>

    const form = useForm<SchemaType>({
      resolver: zodResolver(schema),
      defaultValues: getDefaultValues() as SchemaType,
      mode: 'onChange'
    })
    const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

    // Expose handleValidationErrors to parent via ref
    useImperativeHandle(ref, () => ({
      handleValidationErrors: (error: any) => {
        setValidationErrors(error, form.setError)
        scrollToFirstError()
      },
      reset: () => form.reset()
    }))

    // Update form values when initialData or locales change
    useEffect(() => {
      if (locales.length > 0) {
        const newDefaultValues = getDefaultValues()
        form.reset(newDefaultValues)
      }
    }, [getDefaultValues, locales, form])

    // Sync pictures and files state when initialData changes
    useEffect(() => {
      if (initialData?.heroPicture) {
        setHeroPicture(initialData.heroPicture)
      }
      if (initialData?.pictures) {
        setPictures(initialData.pictures.map(p => p.file))
      }
      if (initialData?.files) {
        setFiles(initialData.files.map(f => f.file))
      }
    }, [initialData])

    const formErrors = form.formState.errors

    const handleSubmit = async (data: SchemaType) => {
      // Prepare pictures data
      const picturesData: PagePictureParams[] = pictures.map((file, index) => ({
        fileId: file.id,
        order: index + 1,
        isMain: !!file.isMain // Use the isMain property from the file object
      }))

      // Prepare files data
      const filesData: PageFileParams[] = files.map((file, index) => ({
        fileId: file.id,
        order: index + 1
      }))

      const submissionData: CreatePageParams = {
        translations: {},
        heroPictureId: heroPicture?.id || '',
        pictures: picturesData,
        files: filesData
      }

      // Add translations
      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          title: data.translations[locale.fullCode]?.title || '',
          content: data.translations[locale.fullCode]?.content || ''
        }
      })

      onSubmit(submissionData)
    }

    const handleSubmitAndNew = async (data: SchemaType) => {
      // Prepare pictures data
      const picturesData: PagePictureParams[] = pictures.map((file, index) => ({
        fileId: file.id,
        order: index + 1,
        isMain: !!file.isMain // Use the isMain property from the file object
      }))

      // Prepare files data
      const filesData: PageFileParams[] = files.map((file, index) => ({
        fileId: file.id,
        order: index + 1
      }))

      const submissionData: CreatePageParams = {
        translations: {},
        heroPictureId: heroPicture?.id || '',
        pictures: picturesData,
        files: filesData
      }

      // Add translations
      locales.forEach(locale => {
        submissionData.translations[locale.fullCode] = {
          title: data.translations[locale.fullCode]?.title || '',
          content: data.translations[locale.fullCode]?.content || ''
        }
      })

      onSubmitAndNew?.(submissionData)
    }

    if (!metaState.response?.locales) {
      return <LoadingIndicator />
    }

    const isCreateModeWithSubmitAndNew = mode === FormModeEnum.CREATE && !!onSubmitAndNew

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(
            isCreateModeWithSubmitAndNew ? handleSubmitAndNew : handleSubmit,
            scrollToFirstError
          )}
          className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpenTextIcon className="h-5 w-5" />
                {mode === FormModeEnum.CREATE ? t('create_page') : t('edit_page')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Translations */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <LanguagesIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">{t('translations')}</h3>
                </div>

                {locales.length > 0 ? (
                  <Tabs
                    defaultValue={locales[0].fullCode}
                    className="w-full"
                    onValueChange={value => {
                      setTimeout(() => {
                        const input = document.querySelector(`[name="translations.${value}.title"]`) as HTMLInputElement
                        if (input) {
                          input.focus()
                        }
                      }, 100)
                    }}>
                    <TabsList className="min-h-10.5 w-full">
                      {locales.map(locale => {
                        const hasError =
                          hasLocaleError(locale.fullCode, 'title') || hasLocaleError(locale.fullCode, 'content')

                        return (
                          <TabsTrigger
                            key={locale.fullCode}
                            value={locale.fullCode}
                            className={`relative flex items-center gap-2 ${
                              hasError ? 'text-destructive border-destructive' : ''
                            }`}>
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={16} />
                            <span className="hidden sm:inline">{locale.name}</span>
                            {hasError && (
                              <div className="bg-destructive absolute -top-1 -right-1 h-2 w-2 animate-pulse rounded-full" />
                            )}
                          </TabsTrigger>
                        )
                      })}
                    </TabsList>

                    {locales.map(locale => {
                      const hasTitleError = hasLocaleError(locale.fullCode, 'title')
                      const hasContentError = hasLocaleError(locale.fullCode, 'content')
                      const hasError = hasTitleError || hasContentError

                      return (
                        <TabsContent
                          key={locale.fullCode}
                          value={locale.fullCode}
                          forceMount
                          className="mt-6 space-y-4 data-[state=inactive]:hidden">
                          <div className="flex items-center gap-2">
                            <Flag country={getCountryCodeFromLocale(locale.fullCode)} size={20} />
                            <h4 className="text-lg font-semibold">{locale.name}</h4>
                            <Badge variant="outline" className="text-xs">
                              {locale.fullCode}
                            </Badge>
                            {hasError && (
                              <Badge variant="destructive" className="animate-pulse text-xs">
                                {t('error')}
                              </Badge>
                            )}
                          </div>

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.title`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel required>{t('title')}</FormLabel>
                                <FormControl>
                                  <Input
                                    placeholder={t('title')}
                                    className={`${hasTitleError ? 'border-destructive focus:border-destructive' : ''}`}
                                    {...field}
                                    value={field.value || ''}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`translations.${locale.fullCode}.content`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel required>{t('content')}</FormLabel>
                                <FormControl>
                                  <Editor
                                    placeholder={t('content')}
                                    value={field.value || ''}
                                    autoFocus={false}
                                    onChange={html => field.onChange(html)}
                                    isError={hasContentError}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      )
                    })}
                  </Tabs>
                ) : (
                  <div className="py-8 text-center">
                    <p>{t('no_locales_available')}</p>
                  </div>
                )}
              </div>

              <Separator />

              {/* Hero Picture */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('hero_picture')}</h3>
                </div>
                <div>
                  <FileSelector
                    multiple={false}
                    onlyImages={true}
                    optimizeImageSelection={true}
                    value={heroPicture}
                    onChange={handleHeroPictureChange}
                    buttonLabel={t('select_picture')}
                    buttonLabelChange={t('change_picture')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={initialData?.heroPicture?.id}
                  />
                </div>
              </div>

              <Separator />

              {/* Pictures */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('pictures')}</h3>
                </div>
                <div>
                  <FileSelector
                    multiple={true}
                    onlyImages={true}
                    value={pictures}
                    onChange={handlePicturesChange}
                    buttonLabel={t('select_pictures')}
                    buttonLabelChange={t('change_pictures')}
                    noSelectionText={t('no_file_selected')}
                    allowMainSelection={true}
                    initialMainId={initialData?.mainPicture?.id}
                  />
                </div>
              </div>

              <Separator />

              {/* Files */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <FileIcon className="h-5 w-5" />
                  <h3 className="font-semibold">{t('files')}</h3>
                </div>
                <div>
                  <FileSelector
                    multiple={true}
                    value={files}
                    onChange={handleFilesChange}
                    buttonLabel={t('select_files')}
                    buttonLabelChange={t('change_files')}
                    noSelectionText={t('no_files_selected')}
                  />
                </div>
              </div>

              <Separator />

              {/* Form actions */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
                  <XIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('cancel')}</span>
                </Button>
                <div className="flex items-center gap-2">
                  {isCreateModeWithSubmitAndNew && (
                    <Button
                      type="button"
                      onClick={() => form.handleSubmit(handleSubmit, scrollToFirstError)()}
                      disabled={isLoading}>
                      <SaveIcon className="h-4 w-4" />
                      <span className="hidden sm:inline">{isLoading ? t('saving') : t('save')}</span>
                    </Button>
                  )}
                  <Button type="submit" disabled={isLoading}>
                    {isCreateModeWithSubmitAndNew ? (
                      <PlusCircleIcon className="h-4 w-4" />
                    ) : (
                      <SaveIcon className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">
                      {isLoading ? t('saving') : isCreateModeWithSubmitAndNew ? t('save_and_add_new') : t(mode)}
                    </span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          <UnsavedChangesGuardDialog
            open={isNavigationBlocked}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
          />
        </form>
      </Form>
    )
  }
)

PageForm.displayName = 'PageForm'

export default PageForm
